import { ArrowRight, Clock, Package, Truck } from "lucide-react";
import { Link } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import { useM3Pipeline, useM3ProneAMagazzino } from "@/hooks/useM3";
import { cn } from "@/lib/utils";

/** Riepilogo della pipeline logistica M3: i 5 stati operativi + flag + CTA. */
export function WidgetPipelineLogistica(_: { widget: unknown }) {
  // Solo pipeline operativa per il chip dei conteggi stati
  const { data, isLoading } = useM3Pipeline({ pipeline_tipo: "effettiva" });
  const { data: pronteData } = useM3ProneAMagazzino();
  const pronteDaPrendere = pronteData?.items.length ?? 0;

  if (isLoading || !data) {
    return <Skeleton className="h-32 w-full" />;
  }

  const stati = [
    { value: "pronto", label: "Pronti", color: "text-blue-700" },
    { value: "avvisato", label: "Avvisati", color: "text-cyan-700" },
    { value: "in_attesa_cliente", label: "Attesa", color: "text-amber-700" },
    { value: "confermato", label: "Confermati", color: "text-emerald-700" },
    { value: "in_carico", label: "In carico", color: "text-violet-700" },
  ];

  const counts = data.counts_per_stato;
  const totalAzionabili =
    (counts.pronto ?? 0) +
    (counts.avvisato ?? 0) +
    (counts.in_attesa_cliente ?? 0) +
    (counts.confermato ?? 0) +
    (counts.in_carico ?? 0);

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <div className="inline-flex items-center gap-1.5 text-sm font-medium">
          <Truck className="size-4 text-muted-foreground" />
          {totalAzionabili} commesse in pipeline
        </div>
        <Link
          to="/moduli/spedizioni"
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
        >
          Apri <ArrowRight className="size-3" />
        </Link>
      </div>

      <div className="space-y-1">
        {stati.map((s) => (
          <Link
            key={s.value}
            to={`/moduli/spedizioni?stato=${s.value}`}
            className="flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-accent/40 transition-colors"
          >
            <span className="text-muted-foreground">{s.label}</span>
            <span className={cn("font-semibold tabular-nums", s.color)}>
              {counts[s.value] ?? 0}
            </span>
          </Link>
        ))}
      </div>

      {pronteDaPrendere > 0 && (
        <Link
          to="/moduli/spedizioni"
          className="block rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 hover:bg-amber-100/60 transition-colors"
        >
          <div className="inline-flex items-center gap-1.5 font-medium">
            <Package className="size-3.5" />
            {pronteDaPrendere} commesse pronte a magazzino non prese in carico
          </div>
          <p className="text-[11px] text-amber-800/80 mt-0.5">
            Trovate nel sistema ma non ancora nella pipeline operativa. Prendile in
            carico dalla tabella in cima a Spedizioni.
          </p>
        </Link>
      )}

      {data.counts_flags.anticipi_in_ritardo > 0 && (
        <Link
          to="/moduli/spedizioni?tipo=anticipo"
          className="block rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900 hover:bg-red-100/60 transition-colors"
        >
          <div className="inline-flex items-center gap-1.5 font-medium">
            <Clock className="size-3.5" />
            {data.counts_flags.anticipi_in_ritardo} anticipi in ritardo sulla data
            prevista
          </div>
          <p className="text-[11px] text-red-800/80 mt-0.5">
            Commesse pre-avvisate al cliente che non sono ancora diventate pronte.
            Verifica se serve risolleciare o annullare.
          </p>
        </Link>
      )}

      {(data.counts_pipeline_tipo.anticipo ?? 0) > 0 && (
        <Link
          to="/moduli/spedizioni?tipo=anticipo"
          className="block text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <Clock className="size-3" /> {data.counts_pipeline_tipo.anticipo} anticipi
          attivi <ArrowRight className="size-3" />
        </Link>
      )}
    </div>
  );
}
